import {
  PrimaryButton,
  SecondaryButton,
  Text,
  useNotifications,
} from '@joor/design-system'
import { useNavigate } from 'react-router-dom'

import { formatDate } from 'utils/dates'

import { useUpsertBillingContacts } from 'modals/BillingContactModal/billingContactModal.hooks'

import { ModalIds } from '../../billingContactModal.ids'
import messages from '../../billingContactModal.messages'
import useStore from '../../billingContactModal.store'
import { areBillingContactsValid } from '../../billingContactModal.utils'
import styles from './Actions.module.css'

interface ActionsProps {
  closeModal: () => void
  organizationId: string
  fromPhp: boolean
  lastUpdatedAt?: string | null
}

const Actions = ({
  closeModal,
  organizationId,
  fromPhp,
  lastUpdatedAt,
}: ActionsProps): JSX.Element => {
  const { addToasts } = useNotifications()
  const navigate = useNavigate()
  const { upsertBillingContacts } = useUpsertBillingContacts()

  const isLoading = useStore((state) => state.isLoading)
  const billingContacts = useStore((state) => state.billingContacts)
  const setIsLoading = useStore((state) => state.setIsLoading)

  const areAllContactsValid = areBillingContactsValid(billingContacts)
  const lastUpdatedDate = lastUpdatedAt ? formatDate(lastUpdatedAt) : ''

  const handleSave = async (): Promise<void> => {
    setIsLoading(true)

    const { error } = await upsertBillingContacts(organizationId)

    if (error) {
      setIsLoading(false)
      return addToasts([
        {
          title: messages.billingContactsSaveErrorTitle,
          description: messages.billingContactsSaveErrorDescription,
          type: 'error',
          shouldCloseManually: true,
        },
      ])
    }

    addToasts([
      {
        title: messages.billingContactsSavedTitle,
        description: messages.billingContactsSavedDescription,
        type: 'success',
        shouldCloseManually: true,
      },
    ])
    closeModal()
    if (fromPhp) {
      navigate(0) // refresh the page if coming from php
    }
  }

  return (
    <div className={styles.ActionsContainer}>
      {lastUpdatedDate && (
        <Text
          id={ModalIds.LastUpdated}
          data-testid={ModalIds.LastUpdated}
          className={styles.LastUpdated}
          letterSpacing="tighter"
          color="primary-500"
        >
          {messages.lastUpdated(lastUpdatedDate)}
        </Text>
      )}
      <SecondaryButton
        id={ModalIds.CancelButton}
        data-testid={ModalIds.CancelButton}
        disabled={isLoading}
        onClick={() => closeModal()}
      >
        {messages.cancel}
      </SecondaryButton>
      <PrimaryButton
        id={ModalIds.SaveButton}
        data-testid={ModalIds.SaveButton}
        className={styles.SaveButton}
        disabled={isLoading || !areAllContactsValid}
        loading={isLoading}
        onClick={handleSave}
      >
        {messages.save}
      </PrimaryButton>
    </div>
  )
}

export default Actions
